import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { apiFetch } from '../lib/api';
import SaveIcon from '@mui/icons-material/Save';
import { PageHeader } from '../components/PageHeader';
import { formatShort, payPeriodFor, today as todayIso } from '../lib/dates';

/**
 * Company details.
 *
 * One row, and the pay-cycle anchor on it decides the boundaries of every pay
 * period in the app — so the screen shows what the current period works out to,
 * rather than leaving that to be discovered on the Timesheet.
 */

interface Company {
    readonly companyName: string;
    readonly abn: string | null;
    readonly streetName: string | null;
    readonly suburbName: string | null;
    readonly postcode: string | null;
    readonly state: string | null;
    readonly country: string | null;
    readonly firstName: string | null;
    readonly lastName: string | null;
    readonly emailAddress: string | null;
    readonly directPhoneNumber: string | null;
    readonly phoneNumber: string | null;
    readonly payrollFirstPayDate: string | null;
    readonly payrollDays: number;
    readonly salesTaxDefault: string | null;
    readonly invoicingDefaultType: string | null;
}

const STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export function CompanyDetails(): React.JSX.Element {
    const [company, setCompany] = useState<Company | null>(null);
    const [form, setForm] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    const today = useMemo(() => todayIso(), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        apiFetch<Company>('/company')
            .then((r) => {
                if (!cancelled) {
                    setCompany(r);
                    setForm({});
                    setError(null);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load company details');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [nonce]);

    const value = (key: keyof Company): string => {
        const override = form[key];
        if (override !== undefined) {
            return override;
        }
        const v = company?.[key];
        return v === null || v === undefined ? '' : String(v);
    };

    const set = (key: keyof Company) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
    };

    const dirty = Object.keys(form).length > 0;

    /**
     * What the anchor and length actually produce.
     *
     * Shown live as the fields change, because "2018-08-16, 14 days" does not
     * read as "Monday to Sunday" to anybody, and getting it wrong moves
     * every pay period in the app.
     */
    const cycle = useMemo(() => {
        const anchor = value('payrollFirstPayDate');
        const days = Number(value('payrollDays'));
        if (anchor === '' || !Number.isFinite(days) || days < 1) {
            return null;
        }
        const period = payPeriodFor(today, anchor, days);
        // The period's own first day, not the anchor's weekday: periods start on
        // the Monday of the anchor's week (see payPeriodFor).
        const [y, m, d] = period.from.split('-').map(Number);
        const startDay = DAY_NAMES[new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay()];
        const [ey, em, ed] = period.to.split('-').map(Number);
        const endDay = DAY_NAMES[new Date(Date.UTC(ey ?? 1970, (em ?? 1) - 1, ed ?? 1)).getUTCDay()];
        return { period, startDay, endDay, days };
    }, [form, company, today]);

    function save(): void {
        setSaving(true);
        setError(null);
        const body: Record<string, unknown> = { ...form };
        if (typeof body['payrollDays'] === 'string') {
            body['payrollDays'] = Number(body['payrollDays']);
        }
        apiFetch<Company>('/company', { method: 'PATCH', body: JSON.stringify(body) })
            .then(() => {
                setNotice('Saved');
                setNonce((n) => n + 1);
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function discard(): void {
        setForm({});
        setError(null);
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <PageHeader title="Company Details" subtitle="Business details, pay cycle and invoicing defaults" />

            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="h6" fontWeight={700}>
                            {company?.companyName ?? 'Company'}
                        </Typography>
                        {dirty ? (
                            <Chip size="small" color="warning" variant="outlined" label="Unsaved changes" />
                        ) : null}
                        {loading ? <CircularProgress size={18} /> : null}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" color="inherit" onClick={discard} disabled={!dirty || saving}>
                            Discard
                        </Button>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={!dirty || saving}>
                            Save Changes
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            ) : null}
            {notice !== null ? (
                <Alert
                    severity="success"
                    sx={{ mb: 2 }}
                    onClose={() => {
                        setNotice(null);
                    }}
                >
                    {notice}
                </Alert>
            ) : null}

            {company === null ? null : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                        gap: 3,
                        alignItems: 'start',
                    }}
                >
                    <Stack gap={3}>
                        {/* ─── Company ────────────────────────────────────── */}
                        <Section title="Company" subtitle="Shown on documents and sent to Xero">
                            <Stack gap={2}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                    <TextField
                                        label="Company name"
                                        size="small"
                                        fullWidth
                                        required
                                        value={value('companyName')}
                                        onChange={set('companyName')}
                                    />
                                    <TextField
                                        label="ABN"
                                        size="small"
                                        sx={{ width: { sm: 220 } }}
                                        value={value('abn')}
                                        onChange={set('abn')}
                                        helperText="Spaces are fine"
                                    />
                                </Stack>
                                <TextField
                                    label="Street"
                                    size="small"
                                    value={value('streetName')}
                                    onChange={set('streetName')}
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                    <TextField
                                        label="Suburb"
                                        size="small"
                                        fullWidth
                                        value={value('suburbName')}
                                        onChange={set('suburbName')}
                                    />
                                    <TextField
                                        label="Postcode"
                                        size="small"
                                        sx={{ width: { sm: 140 } }}
                                        value={value('postcode')}
                                        onChange={set('postcode')}
                                    />
                                    <TextField
                                        select
                                        label="State"
                                        size="small"
                                        sx={{ width: { sm: 120 } }}
                                        value={value('state')}
                                        onChange={set('state')}
                                    >
                                        <MenuItem value="">—</MenuItem>
                                        {STATES.map((s) => (
                                            <MenuItem key={s} value={s}>
                                                {s}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                            </Stack>
                        </Section>

                        {/* ─── Contact ────────────────────────────────────── */}
                        <Section title="Contact" subtitle="Who to talk to about the account">
                            <Stack gap={2}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                    <TextField
                                        label="First name"
                                        size="small"
                                        fullWidth
                                        value={value('firstName')}
                                        onChange={set('firstName')}
                                    />
                                    <TextField
                                        label="Last name"
                                        size="small"
                                        fullWidth
                                        value={value('lastName')}
                                        onChange={set('lastName')}
                                    />
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                    <TextField
                                        label="Email"
                                        size="small"
                                        fullWidth
                                        value={value('emailAddress')}
                                        onChange={set('emailAddress')}
                                    />
                                    <TextField
                                        label="Phone"
                                        size="small"
                                        fullWidth
                                        value={value('directPhoneNumber')}
                                        onChange={set('directPhoneNumber')}
                                    />
                                </Stack>
                            </Stack>
                        </Section>
                    </Stack>

                    <Stack gap={3}>
                        {/* ─── Pay cycle ──────────────────────────────────── */}
                        <Section title="Pay cycle" subtitle="Sets the pay period boundaries on the Timesheet">
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="flex-start">
                                <TextField
                                    label="First pay date"
                                    type="date"
                                    size="small"
                                    value={value('payrollFirstPayDate')}
                                    onChange={set('payrollFirstPayDate')}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    helperText="The anchor every period counts from"
                                />
                                <TextField
                                    label="Days in a period"
                                    size="small"
                                    sx={{ width: { sm: 160 } }}
                                    value={value('payrollDays')}
                                    onChange={set('payrollDays')}
                                />
                            </Stack>

                            {cycle !== null ? (
                                <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Pay periods run
                                    </Typography>
                                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                        <Chip
                                            color="primary"
                                            label={`${cycle.startDay ?? ''} → ${cycle.endDay ?? ''}`}
                                        />
                                        <Typography variant="body2">{cycle.days} days</Typography>
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                        Current period: <strong>{formatShort(cycle.period.from)}</strong> –{' '}
                                        <strong>{formatShort(cycle.period.to)}</strong>
                                    </Typography>
                                </Box>
                            ) : null}

                            <Alert severity="info" sx={{ mt: 2 }}>
                                Changing either field moves every pay period boundary on the Timesheet. The legacy table
                                also stored the start and finish weekday separately, which could disagree with the
                                anchor — they are worked out from it now.
                            </Alert>
                        </Section>

                        {/* ─── Xero defaults ──────────────────────────────── */}
                        <Section title="Invoicing defaults" subtitle="Used when invoices are pushed to Xero">
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                <TextField
                                    select
                                    label="Sales tax"
                                    size="small"
                                    fullWidth
                                    value={value('salesTaxDefault')}
                                    onChange={set('salesTaxDefault')}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    <MenuItem value="Exclusive">Tax exclusive</MenuItem>
                                    <MenuItem value="Inclusive">Tax inclusive</MenuItem>
                                </TextField>
                                <TextField
                                    select
                                    label="New invoices are"
                                    size="small"
                                    fullWidth
                                    value={value('invoicingDefaultType')}
                                    onChange={set('invoicingDefaultType')}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    <MenuItem value="DRAFT">Draft</MenuItem>
                                    <MenuItem value="AUTHORISED">Authorised</MenuItem>
                                </TextField>
                            </Stack>
                        </Section>
                    </Stack>
                </Box>
            )}
        </Container>
    );
}

/** One titled panel, as Program Settings lays out each list. */
function Section({
    title,
    subtitle,
    children,
}: {
    readonly title: string;
    readonly subtitle: string;
    readonly children: React.ReactNode;
}): React.JSX.Element {
    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {subtitle}
            </Typography>
            {children}
        </Paper>
    );
}
